import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Brand } from '../../types/brand'
import { BrandsService } from '../../services/api/brandsService'
import { XMarkIcon, CheckCircleIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline'
import { useDebounce } from '../../hooks/useDebounce'

interface BrandValidationDialogProps {
  isOpen: boolean
  onClose: () => void
  brand: Brand
  onValidate: (updatedName?: string) => Promise<void>
  onMerge: (mergeWithId: string) => Promise<void>
}

export const BrandValidationDialog: React.FC<BrandValidationDialogProps> = ({
  isOpen,
  onClose,
  brand,
  onValidate,
  onMerge
}) => {
  const [brandName, setBrandName] = useState(brand.name)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Brand[]>([])
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [saving, setSaving] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  
  const debouncedSearch = useDebounce(searchQuery, 500)

  useEffect(() => {
    setBrandName(brand.name)
    setSearchQuery('')
    setSelectedBrand(null)
    setSearchResults([])
  }, [brand, isOpen])

  const searchBrands = useCallback(async (query: string) => {
    setIsSearching(true)
    try {
      const results = await BrandsService.searchBrands(query)
      // Filter out the current brand from results
      const filtered = results.filter(b => b.uuid !== brand.uuid)
      setSearchResults(filtered)
    } catch (error) {
      console.error('Error searching brands:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [brand.uuid])

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 3) {
      searchBrands(debouncedSearch)
    } else {
      setSearchResults([])
    }
  }, [debouncedSearch, searchBrands])

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setSearchResults([])
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleValidate = async () => {
    setSaving(true)
    try {
      const updatedName = brandName !== brand.name ? brandName : undefined
      await onValidate(updatedName)
      onClose()
    } catch (error) {
      console.error('Error validating brand:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleMerge = async () => {
    if (!selectedBrand?.uuid) return
    
    setSaving(true)
    try {
      await onMerge(selectedBrand.uuid)
      onClose()
    } catch (error) {
      console.error('Error merging brands:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleUpperCase = () => {
    setBrandName(brandName.toUpperCase())
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Valider la marque
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Que souhaitez-vous faire avec la marque &ldquo;{brand.name}&rdquo; ?
        </p>

        {/* Validate as New Brand */}
        <div className="border rounded-lg p-4 mb-4 bg-gray-50 dark:bg-gray-900">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
            Valider comme nouvelle marque
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Cette marque sera validée et disponible pour tous les produits.
          </p>
          
          <div className="flex gap-2 mb-4">
            <Input
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="Nom de la marque"
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={handleUpperCase}
              title="Mettre en majuscules"
              className="whitespace-nowrap"
            >
              a→A
            </Button>
          </div>
          
          <Button
            variant="primary"
            onClick={handleValidate}
            disabled={saving || !brandName.trim()}
            className="flex items-center gap-2"
          >
            <CheckCircleIcon className="h-4 w-4" />
            {saving ? 'Validation...' : 'Valider la marque'}
          </Button>
        </div>

        {/* Merge with Existing Brand */}
        <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2">
            Fusionner avec une marque existante
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Recherchez une marque existante pour fusionner les deux.
          </p>
          
          <div className="relative mb-4" ref={searchContainerRef}>
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une marque..."
              className="w-full"
            />
            
            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto z-10">
                {searchResults.map((result) => (
                  <button
                    key={result.uuid}
                    onClick={() => {
                      setSelectedBrand(result)
                      setSearchQuery(result.name)
                      setSearchResults([])
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">
                      {result.name}
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {isSearching && (
              <div className="absolute top-full left-0 right-0 mt-1 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    Recherche...
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {selectedBrand && (
            <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Marque sélectionnée :
              </p>
              <p className="font-medium text-gray-900 dark:text-white">
                {selectedBrand.name}
              </p>
            </div>
          )}
          
          <Button
            variant="primary"
            onClick={handleMerge}
            disabled={saving || !selectedBrand}
            className="flex items-center gap-2"
          >
            <ArrowsRightLeftIcon className="h-4 w-4" />
            {saving ? 'Fusion...' : 'Fusionner les marques'}
          </Button>
        </div>

        <div className="flex justify-end mt-6">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </Button>
        </div>
      </div>
    </div>
  )
}